import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MailCheck } from 'lucide-react';
import { VsCodeAuthContext } from '../contextApis/vsCodeAuthContext';
import { AuthInput, BrandPanel } from './vsCodeSignIn';
import '../styles/sixthDesign.css';

const RESEND_COOLDOWN = 60; // matches backend cooldown

const VsCodeVerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { error, loading, setError, handleVerifyEmail, handleResendVerification } =
    useContext(VsCodeAuthContext);

  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [resent, setResent] = useState(false);
  const email = localStorage.getItem('pending_verification_email') || '';
  const timerRef = useRef(null);

  // No pending email -> nothing to verify here.
  useEffect(() => {
    if (!email) navigate(`/vscode/signup${location.search}`);
  }, [email, navigate, location.search]);

  // Resend cooldown ticker (a code was just sent by signup/signin).
  useEffect(() => {
    timerRef.current = setInterval(
      () => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (otp.trim().length < 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    // On success this completes the normal auth flow (incl. VS Code handoff).
    await handleVerifyEmail(email, otp.trim());
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setError(null);
    const ok = await handleResendVerification(email);
    if (ok) {
      setResent(true);
      setCooldown(RESEND_COOLDOWN);
    }
  };

  return (
    // .sixth-design carries the design tokens (--bg/--text/--accent-*); without
    // it every var() below resolves to nothing and the page renders unreadable.
    <div className="sixth-design sd-page-enter" style={{ minHeight: '100vh' }}>
      <div className="sd-auth-shell" style={{
        minHeight: '100vh', display: 'grid', gridTemplateColumns: '1.05fr 1fr',
      }}>
        <BrandPanel kind="signup" />

        <div style={{ padding: '60px 44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <div style={{ marginBottom: 30 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, marginBottom: 18,
                border: '1px solid var(--line)', background: 'rgba(255,255,255,0.03)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MailCheck size={20} style={{ color: 'var(--accent-2)' }} />
              </div>
              <h1 style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.025em', margin: '0 0 8px', color: 'var(--text)' }}>
                Check your email
              </h1>
              <p style={{ fontSize: 14.5, color: 'var(--text-2)', margin: 0, lineHeight: 1.55 }}>
                We sent a 6-digit code to{' '}
                <span style={{ color: 'var(--text)' }}>{email}</span>.
                Enter it below to verify your account.
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{
                    marginBottom: 16, padding: '10px 14px', borderRadius: 10,
                    background: 'rgba(255,107,107,0.06)',
                    border: '1px solid rgba(255,107,107,0.2)',
                    color: 'var(--danger)', fontSize: 13, textAlign: 'center',
                  }}
                >
                  {error}
                </motion.div>
              )}
              {resent && !error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{
                    marginBottom: 16, padding: '10px 14px', borderRadius: 10,
                    background: 'rgba(128,255,170,0.05)',
                    border: '1px solid rgba(128,255,170,0.18)',
                    color: 'var(--text-2)', fontSize: 13, textAlign: 'center',
                  }}
                >
                  A new code is on its way.
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <AuthInput
                label="Verification code"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                autoFocus
              />
              <button
                type="submit"
                className="sd-btn sd-btn-accent"
                disabled={loading || otp.length < 6}
                style={{ height: 46, width: '100%', fontSize: 14.5 }}
              >
                {loading ? <span className="sd-spinner" /> : 'Verify email'}
              </button>
            </form>

            <div style={{ marginTop: 22, fontSize: 13, color: 'var(--text-2)', textAlign: 'center' }}>
              Didn't get it?{' '}
              <button
                type="button"
                onClick={resend}
                disabled={cooldown > 0}
                style={{
                  background: 'none', border: 0, fontFamily: 'inherit', fontSize: 13,
                  color: cooldown > 0 ? 'var(--text-2)' : 'var(--accent-2)',
                  cursor: cooldown > 0 ? 'default' : 'pointer',
                  textDecoration: cooldown > 0 ? 'none' : 'underline',
                  textUnderlineOffset: 3, padding: 0,
                }}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
              {' '}· Check your spam folder too.
            </div>

            <div style={{ marginTop: 14, fontSize: 13, color: 'var(--text-2)', textAlign: 'center' }}>
              Wrong email?{' '}
              <Link
                to={`/vscode/signup${location.search}`}
                style={{ color: 'var(--accent-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}
              >
                Start over
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VsCodeVerifyEmail;
